import React from "react";
import "./HomeHero.css";

// Brand-pastel mana motes around the hero title (the "cast point"). Night motes
// converge on the title; day motes are gold dust rising through the window
// light. Two layers create depth: "back" sits behind the text (bigger, blurred,
// slower — far away), "front" drifts over it (smaller, sharper, faster).
// Memoized like StarBackground so the random field survives re-renders.
const MOTE_COLORS = {
  day: ["#fed7aa", "#fde68a", "#fbcfe8", "#fff7ed"],
  night: ["#8fd3ff", "#99f6e4", "#a7f3d0", "#f8fafc"],
};

const moteTravel = (scene) => {
  if (scene === "day") {
    const fromX = (Math.random() * 2 - 1) * 340;
    const fromY = -10 + Math.random() * 110;
    return {
      fromX,
      fromY,
      toX: fromX + (Math.random() * 2 - 1) * 40,
      toY: fromY - (90 + Math.random() * 90),
      toScale: 0.6,
    };
  }
  const angle = Math.random() * Math.PI * 2;
  const radius = 140 + Math.random() * 220;
  return {
    fromX: Math.cos(angle) * radius,
    fromY: Math.sin(angle) * radius * 0.55,
    toX: 0,
    toY: 0,
    toScale: 0.12,
  };
};

const EnergyField = React.memo(({ layer, count, scene }) => {
  const isFront = layer === "front";
  const colors = MOTE_COLORS[scene] || MOTE_COLORS.night;
  const baseDuration = scene === "day" ? (isFront ? 6 : 8) : (isFront ? 4 : 5.5);
  const motes = Array.from({ length: count }).map((_, index) => {
    const travel = moteTravel(scene);
    const size = isFront ? 2 + Math.random() * 2.5 : 3.5 + Math.random() * 3.5;
    return (
      <span
        key={index}
        className="home-energy-mote"
        style={{
          "--from-x": `${travel.fromX}px`,
          "--from-y": `${travel.fromY}px`,
          "--to-x": `${travel.toX}px`,
          "--to-y": `${travel.toY}px`,
          "--to-scale": travel.toScale,
          "--peak-opacity": (isFront ? 0.55 : 0.8) + Math.random() * 0.2,
          width: `${size}px`,
          height: `${size}px`,
          background: colors[Math.floor(Math.random() * colors.length)],
          animationDuration: `${baseDuration + Math.random() * 4}s`,
          animationDelay: `${-Math.random() * 9}s`,
        }}
      />
    );
  });
  return <div aria-hidden="true" className={`home-energy-field home-energy-field--${layer}`}>{motes}</div>;
});

// Night shows all six glints as a starry sky; day keeps the first three.
const GLINTS = [
  { left: "4%", top: "-34%" },
  { left: "88%", top: "-42%" },
  { left: "101%", top: "58%" },
  { left: "-3%", top: "64%" },
  { left: "30%", top: "-60%" },
  { left: "72%", top: "112%" },
];

// Soft "mana veil" behind the title — two pre-blurred radial washes drifting
// slowly in opposite phases, plus four-point star glints twinkling around the
// cast point; by day, a window-light shaft too. Atmosphere instead of geometry:
// no strokes, and the gradients are soft by construction (no filter cost).
const TitleVeil = ({ scene }) => (
  <div aria-hidden="true">
    <div className="home-hero-aurora home-hero-aurora--a" />
    <div className="home-hero-aurora home-hero-aurora--b" />
    {scene === "day" ? <div className="home-hero-shaft" /> : null}
    {GLINTS.slice(0, scene === "day" ? 3 : GLINTS.length).map((position) => (
      <span key={`${position.left}:${position.top}`} className="home-hero-glint" style={position} />
    ))}
  </div>
);

// CJK text may break between any two characters, which can split a word
// ("再 / 利用", "視覺風 / 格"). Each clause up to its punctuation is set as its
// own span, which the CSS keeps on one line for CJK whenever it fits.
const Clauses = ({ text }) =>
  (text.match(/[^、，：；。]+[、，：；。]?/g) || [text]).map((clause, index) => (
    <span key={index} className="home-hero-clause">{clause}</span>
  ));

/** Homepage hero: title, subtitle and the CTA row (`children`), styled by scene. */
export default function HomeHero({ scene, title, subtitle, children }) {
  return (
    <section className="home-hero" data-scene={scene}>
      <div className="home-hero-title-wrap">
        <div className="home-hero-glow" aria-hidden="true" />
        <TitleVeil scene={scene} />
        <EnergyField layer="back" count={10} scene={scene} />
        <h1>
          <Clauses text={title} />
        </h1>
        <EnergyField layer="front" count={7} scene={scene} />
      </div>
      <h2>
        <Clauses text={subtitle} />
      </h2>
      <div className="home-hero-actions">{children}</div>
    </section>
  );
}
